import type {
    ApiLoopDistinctFacet,
    ApiLoopSearchFilterType,
    ApiLoopSort,
} from '../api/searchRequests';
import type {
    SearchRequestFacet,
    SearchRequestNestedFilter,
    SortOrderType,
} from '../models/models';

import { SearchFacetType } from '../models/models';

const sortOrderMap: Readonly<Record<SortOrderType, ApiLoopSort<string>>> = {
    popular: { order: 'desc', type: 'popularity' },
    'product_name+asc': {
        order: 'asc',
        attributeName: 'name',
        type: 'attribute',
    },
    'product_effectiveprice_cents+asc': {
        order: 'asc',
        attributeName: 'piecePrice',
        type: 'attribute',
    },
    'product_effectiveprice_cents+desc': {
        order: 'desc',
        attributeName: 'piecePrice',
        type: 'attribute',
    },
    'product_effectiveprice_with_promo+asc': {
        order: 'asc',
        attributeName: 'lowestOnlineSinglePiecePrice',
        type: 'attribute',
    },
    'product_effectiveprice_with_promo+desc': {
        order: 'desc',
        attributeName: 'lowestOnlineSinglePiecePrice',
        type: 'attribute',
    },
    'product_comparativeprice+asc': {
        order: 'asc',
        attributeName: 'comparativeSortPrice',
        type: 'attribute',
    },
    'product_comparativeprice+desc': {
        order: 'desc',
        attributeName: 'comparativeSortPrice',
        type: 'attribute',
    },
};

const facetMap: Record<SearchFacetType, ApiLoopDistinctFacet> = {
    categories: {
        attributeName: 'categories',
        type: 'distinct',
        operator: 'AND',
        name: SearchFacetType.RecipeCategories,
        selected: [],
    },
    brand: {
        attributeName: 'manufacturerName',
        type: 'distinct',
        operator: 'OR',
        name: SearchFacetType.Brand,
        selected: [],
    },
    loyaltybrand: {
        attributeName: 'brand',
        type: 'distinct',
        operator: 'AND',
        name: SearchFacetType.Loyaltybrand,
        selected: [],
    },
    loyaltyCategory: {
        attributeName: 'category',
        type: 'distinct',
        operator: 'AND',
        name: SearchFacetType.LoyaltyCategory,
        selected: [],
    },
    categoryIds: {
        attributeName: 'categoryIds',
        type: 'distinct',
        operator: 'OR',
        name: SearchFacetType.CategoryIds,
        selected: [],
    },
    environmentalLabels: {
        attributeName: 'filterLabels',
        type: 'distinct',
        operator: 'AND',
        name: SearchFacetType.EnvironmentalLabels,
        selected: [],
    },
    topCategory: {
        attributeName: 'topCategory',
        type: 'distinct',
        operator: 'OR',
        name: SearchFacetType.TopCategory,
        selected: [],
    },
};

export const mapSortOrder = (sortOrders: string[]) => {
    const sortBy: ApiLoopSort<string>[] = [];
    if (sortOrders) {
        sortOrders.forEach((sort) => {
            if (sort === 'default') {
                sortBy.push(sortOrderMap.popular);
                return;
            }

            const sortOrder = sortOrderMap[sort as SortOrderType];
            if (sortOrder) {
                sortBy.push(sortOrder);
            } else {
                // eslint-disable-next-line no-console
                console.warn(
                    `mapping for sortOrder: ${sort} not found, possible sort orders: ${Object.keys(
                        sortOrderMap,
                    ).join(',')}`,
                );
            }
        });
    }

    return sortBy;
};

export const mapFacets = (facets: SearchRequestFacet[]) => {
    const loopFacets: ApiLoopDistinctFacet[] = [];
    if (facets) {
        facets.forEach((facet) => {
            const loopFacet = facetMap[facet.type];
            const selectedFacets = facet.selected ? [...facet.selected] : [];
            if (loopFacet) {
                loopFacets.push({
                    ...loopFacet,
                    selected: selectedFacets,
                    operator: facet.operator ?? loopFacet.operator,
                });
            } else {
                // eslint-disable-next-line no-console
                console.warn(
                    `mapping for facet: ${facet.type} not found, possible facets: ${Object.keys(
                        facetMap,
                    ).join(',')}`,
                );
            }
        });
    }

    return loopFacets;
};

export const mapFilter = (filter: SearchRequestNestedFilter): ApiLoopSearchFilterType => {
    if ('and' in filter) {
        return { and: filter.and.map(mapFilter) };
    }
    if ('or' in filter) {
        return { or: filter.or.map(mapFilter) };
    }
    if ('not' in filter) {
        return { not: mapFilter(filter.not) };
    }
    return filter;
};
